// What a connect or reconnect re-checks, and how the server's answer and the
// brand reads after it are judged. Pure; used by useOAuthConnect, which runs the
// flow for the shared ConnectionDialog on the Analytics and Connections pages.
// (Moved from analytics/shared/ReconnectDialog.tsx, which re-exports them.)

import { isLiveLease } from '@/components/analytics/data/freshness';
import { PLATFORM_LABEL, type AnalyticsPlatform, type BrandAnalytics, type RefreshResult } from '@/components/analytics/data/types';
import type { ConnectMode, OAuthProvider } from './connect';

/** Which sign-in fixes a platform: Instagram rides on the Facebook Page. */
export const PROVIDER_OF: Record<AnalyticsPlatform, OAuthProvider> = {
  facebook: 'facebook',
  instagram: 'facebook',
  linkedin: 'linkedin',
  ga4: 'google',
};

/** A provider's main platform: the dialog to open for a result that names only
 *  its provider (a popup an older build opened). */
export const PLATFORM_OF_PROVIDER: Record<OAuthProvider, AnalyticsPlatform> = { facebook: 'facebook', linkedin: 'linkedin', google: 'ga4' };

export const PROVIDER_LABEL: Record<OAuthProvider, string> = { facebook: 'Facebook', linkedin: 'LinkedIn', google: 'Google Analytics' };

/** How long to wait for the re-check before saying it is taking a while. */
export const RECHECK_PATIENCE_MS = 90_000;

/** What a re-check waits for. The server stamps last_attempt_at when it claims
 *  a run, so a newer stamp than the one read before asking is that run — a
 *  read that left before the claim can't pass for the answer. */
export interface Recheck {
  /** last_attempt_at per platform in the read taken just before asking. */
  before: Partial<Record<AnalyticsPlatform, string | null>>;
  /** Whether that read already showed the platform's lease: then the run
   *  the server found going is the one that read saw. */
  leaseSeen: Partial<Record<AnalyticsPlatform, boolean>>;
  /** A run was started: wait for its stamp and for its lease to end. */
  started: AnalyticsPlatform[];
  /** Another run held the lease. It may have begun before the new sign-in, so
   *  once it ends Maloo asks again — the server only starts another run when
   *  the connection changed after that one began. Kept here until that
   *  second answer is in. */
  inFlight: AnalyticsPlatform[];
  /** Asked again and found running again: that run began after the sign-in,
   *  so its result is the answer. */
  rerun: AnalyticsPlatform[];
  /** Not re-checked now (backing off after a failure, or checked minutes
   *  ago): judged by the status on file. */
  notNow: AnalyticsPlatform[];
  /** Asked for, but the server no longer has it connected (the sign-in left
   *  it out, e.g. Facebook without the Instagram account). */
  gone: AnalyticsPlatform[];
}

export type Outcome =
  | { platform: AnalyticsPlatform; kind: 'ok' }
  | { platform: AnalyticsPlatform; kind: 'broken'; reason: string | null }
  | { platform: AnalyticsPlatform; kind: 'later' }
  | { platform: AnalyticsPlatform; kind: 'gone' };

/** The pipeline's reasons end with where to fix them ("— reconnect it in
 *  Settings"); here the fix is the button below. */
export function reasonHere(reason: string | null | undefined): string | null {
  if (!reason) return null;
  const cut = reason.lastIndexOf(' — ');
  return / in Settings\.?$/.test(reason) && cut > 0 ? `${reason.slice(0, cut)}.` : reason;
}

/** The platforms one sign-in re-checks: every connected one it feeds. A first
 *  Facebook connect asks for both Facebook and Instagram (the Page may bring an
 *  Instagram account along); with nothing connected to go by — no read of the
 *  brand, or none of its platforms — it asks for `platform` itself. */
export function platformsFor(
  platform: AnalyticsPlatform,
  brand: BrandAnalytics | undefined,
  mode: ConnectMode = 'reconnect',
): AnalyticsPlatform[] {
  if (PROVIDER_OF[platform] !== 'facebook') return [platform];
  if (mode === 'connect') return ['facebook', 'instagram'];
  const connected = (['facebook', 'instagram'] as const).filter((p) => brand?.connections[p]);
  return connected.length > 0 ? connected : [platform];
}

/** The server's answer to a re-check of `asked`, as what to wait for. */
export function recheckFrom(
  asked: AnalyticsPlatform[],
  result: RefreshResult,
  before: Recheck['before'],
  leaseSeen: Recheck['leaseSeen'] = {},
): Recheck {
  const inFlight = result.skipped.filter((s) => s.reason === 'in_flight').map((s) => s.platform);
  const answered = new Set([...result.started, ...result.skipped.map((s) => s.platform)]);
  return {
    before,
    leaseSeen,
    started: result.started,
    inFlight,
    rerun: [],
    notNow: result.skipped.filter((s) => s.reason !== 'in_flight').map((s) => s.platform),
    // The server answers for every platform it has connected, so one it left
    // out isn't connected any more — not something it will retry.
    gone: asked.filter((p) => !answered.has(p)),
  };
}

/** Folds the answer to asking again for `again` into the check. */
export function mergeAskAgain(check: Recheck, again: AnalyticsPlatform[], next: Recheck): Recheck {
  return {
    before: { ...check.before, ...next.before },
    leaseSeen: { ...check.leaseSeen, ...next.leaseSeen },
    started: [...check.started, ...next.started],
    inFlight: check.inFlight.filter((p) => !again.includes(p)),
    rerun: [...check.rerun, ...next.inFlight],
    notNow: [...check.notNow, ...next.notNow],
    gone: [...check.gone, ...next.gone],
  };
}

/** Where a re-check stands in one brand read: still waiting (with any
 *  platforms to ask again for), or settled, with how each platform came out. */
export function judgeRecheck(
  check: Recheck,
  brand: BrandAnalytics,
  readAt: number,
  now: number = Date.now(),
): { settled: false; askAgain: AnalyticsPlatform[] } | { settled: true; outcomes: Outcome[] } {
  const row = (p: AnalyticsPlatform) => brand.sync.find((s) => s.platform === p);
  const live = (p: AnalyticsPlatform) => {
    const s = row(p);
    return !!s && isLiveLease(s, readAt, now);
  };
  /** This read has seen the run the server answered about: a newer stamp than
   *  the read before asking, or that read already showed its lease. A read
   *  from before the answer shows neither, and can't pass for its end. */
  const seen = (p: AnalyticsPlatform) => {
    const s = row(p);
    return !!s && (s.last_attempt_at !== (check.before[p] ?? null) || !!check.leaseSeen[p]);
  };
  const ended = (p: AnalyticsPlatform) => seen(p) && !live(p);
  const askAgain = check.inFlight.filter(ended);
  const waiting =
    check.inFlight.length > 0 ||
    !check.rerun.every(ended) ||
    check.started.some((p) => {
      const s = row(p);
      return !s || s.last_attempt_at === (check.before[p] ?? null) || live(p);
    });
  if (waiting) return { settled: false, askAgain };

  const judged = (p: AnalyticsPlatform, checked: boolean): Outcome => {
    const s = row(p);
    if (s?.status === 'ok') return { platform: p, kind: 'ok' };
    return checked ? { platform: p, kind: 'broken', reason: reasonHere(s?.reason) } : { platform: p, kind: 'later' };
  };
  return {
    settled: true,
    outcomes: [
      ...[...check.started, ...check.rerun].map((p) => judged(p, true)),
      ...check.notNow.map((p) => judged(p, false)),
      ...check.gone.map((p): Outcome => ({ platform: p, kind: 'gone' })),
    ],
  };
}

/** A first Facebook connect asks about Instagram too; a Page without an
 *  Instagram account answers "gone", which is news, not a failure — unless
 *  Instagram was what the member came to connect. */
export function instagramNotLinked(o: Outcome, platform: AnalyticsPlatform, mode: ConnectMode): boolean {
  return mode === 'connect' && platform !== 'instagram' && o.platform === 'instagram' && o.kind === 'gone';
}

/** One platform's line when a re-check comes out mixed. */
export function outcomeLine(o: Outcome, mode: ConnectMode = 'reconnect'): string {
  const label = PLATFORM_LABEL[o.platform];
  if (o.kind === 'ok') return mode === 'connect' ? `${label}: connected.` : `${label}: working again.`;
  if (o.kind === 'broken') return `${label}: ${o.reason ?? (mode === 'connect' ? "isn't working yet." : 'still not working.')}`;
  if (o.kind === 'gone') {
    if (o.platform !== 'instagram') return `${label}: not connected${mode === 'connect' ? '' : ' any more'} — sign in again.`;
    return mode === 'connect'
      ? "Instagram: no Instagram account is linked to that Page. Link one in the Page's settings, then connect again."
      : "Instagram: not linked any more — that Facebook sign-in didn't include the Instagram account. Sign in again and keep it selected.";
  }
  return `${label}: not re-checked yet — Maloo retries it automatically.`;
}

/** "LinkedIn", "Facebook and Instagram". */
export const labelsOf = (platforms: AnalyticsPlatform[]) => platforms.map((p) => PLATFORM_LABEL[p]).join(' and ');

/** Prompts to reconnect, one per sign-in. Facebook and Instagram share Meta's:
 *  when both need it they get ONE prompt, which opens Facebook's dialog — its
 *  re-check covers both — placed where the first of the two was. Otherwise
 *  each item is its own prompt, in the order given. */
export function bySignIn<T extends { platform: AnalyticsPlatform }>(items: T[]): Array<{ open: AnalyticsPlatform; items: T[] }> {
  const facebook = items.find((i) => i.platform === 'facebook');
  const instagram = items.find((i) => i.platform === 'instagram');
  const groups: Array<{ open: AnalyticsPlatform; items: T[] }> = [];
  for (const item of items) {
    if (facebook && instagram && (item === facebook || item === instagram)) {
      if (item === (items.indexOf(facebook) < items.indexOf(instagram) ? facebook : instagram)) {
        groups.push({ open: 'facebook', items: [facebook, instagram] });
      }
      continue;
    }
    groups.push({ open: item.platform, items: [item] });
  }
  return groups;
}
